import { Injectable } from '@angular/core';
import { AbstractControlOptions, FormArray } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  Parameters,
  PledgeProductRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';

import { MissionParticulier } from '@app/forms/subscription/definition/interface/enum';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(
    entity: PledgeProductRead & { parameters: any },
    option: Parameters,
  ) {
    const parameters: Parameters = {
      ...option,
    };
    let options: AbstractControlOptions = {};
    const missionParticulier: MissionParticulier[] =
      entity?.parameters?.missionParticulier || [];

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        missionParticulier: [entity?.parameters?.missionParticulier || []],
        IRPPOption: [
          missionParticulier.includes(MissionParticulier.IRPP) || null,
        ],
        IFIOption: [
          missionParticulier.includes(MissionParticulier.IFI) || null,
        ],
        accountingTakeoverOption: [
          missionParticulier.includes(MissionParticulier.ACCOUNTING_TAKEOVER) ||
            null,
        ],
        packEssentielOption: [
          missionParticulier.includes(MissionParticulier.PACK_ESSENTIEL) ||
            null,
        ],
        packEssentielRevenusFonciersOption: [
          missionParticulier.includes(
            MissionParticulier.PACK_ESSENTIEL_REVENUS_FONCIERS,
          ),
        ],
        packEssentielRevenusFonciersAvecDispositifOption: [
          missionParticulier.includes(
            MissionParticulier.PACK_ESSENTIEL_REVENUS_FONCIERS_AVEC_DISPOSITIF,
          ),
        ],

        packEssentiel: [entity?.parameters?.packEssentiel || null],
        packEssentielRevenusFonciers: [
          entity?.parameters?.packEssentielRevenusFonciers || null,
        ],
        packEssentielRevenusFonciersAvecDispositif: [
          entity?.parameters?.packEssentielRevenusFonciersAvecDispositif ||
            null,
        ],
        irpp: [entity?.parameters?.irpp || null],
        ifi: [entity?.parameters?.ifi || null],
        accountingTakeover: [entity?.parameters?.accountingTakeover || null],
      }),
    };

    return this.formBuilder.group(formConfig, options);
  }

  generate(entity: any, option: Parameters) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  get products() {
    return this.form.get('products') as FormArray;
  }

  extract(): any[] {
    const formValue = _.cloneDeep(this.form?.value?.products);

    const extractedValues = formValue?.map(item => {
      const missionParticulier = this.extractMissionParticulier(
        item?.parameters,
      );
      const updatedItem = this.removeUnusedFields(item?.parameters);

      return {
        ...item,
        parameters: {
          ...item.parameters,
          ...updatedItem,
          missionParticulier,
        },
      };
    });

    return extractedValues;
  }

  private extractMissionParticulier(item: any): MissionParticulier[] {
    const optionToMissionParticulierMap: { [key: string]: MissionParticulier } =
      {
        IRPPOption: MissionParticulier.IRPP,
        IFIOption: MissionParticulier.IFI,
        accountingTakeoverOption: MissionParticulier.ACCOUNTING_TAKEOVER,
        packEssentielOption: MissionParticulier.PACK_ESSENTIEL,
        packEssentielRevenusFonciersOption:
          MissionParticulier.PACK_ESSENTIEL_REVENUS_FONCIERS,
        packEssentielRevenusFonciersAvecDispositifOption:
          MissionParticulier.PACK_ESSENTIEL_REVENUS_FONCIERS_AVEC_DISPOSITIF,
      };

    const missionParticulier: MissionParticulier[] = [];

    for (const option in optionToMissionParticulierMap) {
      if (
        item[option] &&
        !missionParticulier.includes(optionToMissionParticulierMap[option])
      ) {
        missionParticulier.push(optionToMissionParticulierMap[option]);
      }
    }

    return missionParticulier;
  }

  private removeUnusedFields(item: any): any {
    const fieldsToRemove = [
      'IRPPOption',
      'IFIOption',
      'accountingTakeoverOption',
      'packEssentielOption',
      'packEssentielRevenusFonciersOption',
      'packEssentielRevenusFonciersAvecDispositifOption',
    ];

    return _.omit(item, fieldsToRemove);
  }
}
